'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Box, Center, ScrollArea, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '@/src/modules/auth/stores/use-auth-store';
import { useCreateComplaintMessage } from '@/src/modules/chat/hooks/use-create-complaint-message';
import { useCreateMarketplaceCustomerMessage } from '@/src/modules/chat/hooks/use-create-marketplace-customer-message';
import { useCreateSelfMedicationMessage } from '@/src/modules/chat/hooks/use-create-self-medication-message';
import ChatComposer from '@/src/shared/components/chat/chat-message-composer/chat-composer';
import { ChatMessageList } from '@/src/shared/components/chat/chat-message-list';
import { ChatRoomEmptyState } from '@/src/shared/components/chat/chat-room-empty-state';
import { ChatMessageListSkeleton } from '@/src/shared/components/chat/loading/chat-message-list-skeleton';
import { ApplicationError } from '@/src/shared/errors';
import { useChatRoomScroll } from '@/src/shared/hooks/use-chat-room-scroll';
import { useChatStore } from '@/src/shared/stores/use-chat-store';
import { translateWithFallback } from '@/src/shared/utils/i18n';
import { useMarketplaceCustomerChatContext } from '../../../_contexts';
import { useMarketplaceCustomerWebsocket } from '../../../_hooks/use-marketplace-customer-websocket';
import { ChatFloatingWindowConversationHeader } from '../../ui/chat-popup/chat-floating-window-conversation-header';

interface IChatFloatingWindowRoomProps {
  onClose?: () => void;
}

export function ChatFloatingWindowRoom({
  onClose,
}: IChatFloatingWindowRoomProps) {
  const apiErrorTranslator = useTranslations('api-errors');
  const commonErrorTranslator = useTranslations('common-errors');
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const userId = useAuthStore((state) => state.user?.props.id);
  const invoiceNumber = useChatStore((state) => state.invoiceNumber);

  const {
    selectedConversation,
    currentConversationMessages,
    isGettingMoreCurrentConversationMessages,
    isGettingCurrentConversationMessages,
    selfMedicationConversation,
    selectedConversationId,
    loadMoreCurrentConversationMessages,
    isReachingEndCurrentConversationMessages,
    complaintConversation,
  } = useMarketplaceCustomerChatContext();
  const {
    trigger: sendSelfMedicationMessage,
    isLoading: isSendingSelfMedicationMessage,
  } = useCreateSelfMedicationMessage({ userId: userId! });
  const {
    trigger: sendComplaintMessage,
    isLoading: isSendingComplaintMessage,
  } = useCreateComplaintMessage({ userId: userId! });
  const { trigger: sendCustomerMessage, isLoading: isSendingCustomerMessage } =
    useCreateMarketplaceCustomerMessage({
      userId: userId!,
      conversationId: selectedConversationId!,
    });
  const {
    scrollToBottomScrollArea,
    handleScrollReachTop,
    setCurrentLastScrollHeight,
  } = useChatRoomScroll({
    scrollAreaRef: viewportRef,
    onScrollToTop: () => loadMoreCurrentConversationMessages(),
    isInitialLoading: isGettingCurrentConversationMessages,
    isLoadingMore: isGettingMoreCurrentConversationMessages,
    isReachingEnd: isReachingEndCurrentConversationMessages,
    messages: currentConversationMessages,
  });
  useMarketplaceCustomerWebsocket({ setCurrentLastScrollHeight });

  const isSendingMessage =
    isSendingSelfMedicationMessage ||
    isSendingCustomerMessage ||
    isSendingComplaintMessage;
  const complaintConversationIsSelected =
    selectedConversation?.props.id === complaintConversation?.props.id;
  const selfMedicationConversationIsSelected =
    selectedConversation?.props.id === selfMedicationConversation?.props.id;
  const shouldPreserveComplaintInvoice =
    Boolean(invoiceNumber) &&
    (selectedConversationId === null ||
      selectedConversationId === complaintConversation?.props.id);

  const onSendMessage = async (content: string | null, attachments: File[]) => {
    if (!selectedConversation) {
      return;
    }

    try {
      if (selfMedicationConversationIsSelected) {
        await sendSelfMedicationMessage({ content, attachments });
        return;
      }
      if (complaintConversationIsSelected) {
        await sendComplaintMessage({ content, attachments });
        return;
      }
      await sendCustomerMessage({ content, attachments });
      scrollToBottomScrollArea();
    } catch (error) {
      if (error instanceof ApplicationError) {
        notifications.show({
          title: error.name,
          message: translateWithFallback(
            error.message,
            apiErrorTranslator,
            commonErrorTranslator
          ),
          color: 'red',
        });
        return;
      }

      throw error;
    }
  };

  return (
    <Stack h="100%" gap={0}>
      <ChatFloatingWindowConversationHeader onClose={onClose} />

      {selectedConversation ? (
        <>
          <ScrollArea.Autosize
            bg="gray.1"
            viewportRef={viewportRef}
            onScrollPositionChange={handleScrollReachTop}
            scrollbarSize="0.35rem"
            h="100%"
          >
            {!isGettingCurrentConversationMessages ? (
              <ChatMessageList
                size="sm"
                messages={currentConversationMessages}
                isLoadingMore={isGettingMoreCurrentConversationMessages}
              />
            ) : (
              <ChatMessageListSkeleton size="sm" />
            )}
          </ScrollArea.Autosize>

          <Box py="0.5rem" px="0.75rem">
            <ChatComposer
              size="sm"
              onSendMessage={onSendMessage}
              isSendingMessage={isSendingMessage}
              supportedFileUploads={['image', 'file']}
              captureMode="environment"
              selectedConversationId={selectedConversationId}
              preserveContentOnConversationChange={
                shouldPreserveComplaintInvoice
              }
            />
          </Box>
        </>
      ) : (
        <Center h="100%" mt="-3rem">
          <ChatRoomEmptyState size="sm" />
        </Center>
      )}
    </Stack>
  );
}
